/* 213A · editable interface strings.
   The island is optional and every lookup carries the original literal as its
   fallback, so with no override the byte on screen is the byte P6-R4 shipped. */
const TOS_UI_STRINGS = (() => {
  try {
    const el = typeof document === "undefined" ? null : document.getElementById("tos-ui-strings");
    return el ? JSON.parse(el.textContent || "{}") : {};
  } catch (error) { return {}; }
})();
const TOS_UI = (key, fallback) => {
  const value = TOS_UI_STRINGS[key];
  return typeof value === "string" && value !== "" ? value : fallback;
};
const TOS_UI_T = (key, pattern, args) => TOS_UI(key, pattern)
  .replace(/\{(\d+)\}/g, (whole, index) => (args[Number(index)] === undefined ? whole : String(args[Number(index)])));
import { scheduleBoot } from "./3os-living-r1c-p1-boot-gate.js";
/* ==========================================================================
   The Living Society · 207B–207G
   --------------------------------------------------------------------------
   Progressive enhancement for the final local candidate. This module owns
   only the new route-specific interactions:
     · Picual Taste Instrument
     · mobile story density
     · Open Table concierge

   Commerce, cart, films, consent, forms, loader and route transitions remain
   owned by their existing modules. No Shopify mutation exists here.
   ========================================================================== */

const root = document.documentElement;
const $ = (selector, scope = document) => scope?.querySelector(selector) ?? null;
const $$ = (selector, scope = document) => [...(scope?.querySelectorAll(selector) ?? [])];

root.classList.add("living-207");

/* --------------------------------------------------------------------------
   207B · Picual Taste Instrument
   The values are carried by the source-locked markup. This controller never
   derives a radar score or a health interpretation from laboratory data.
   -------------------------------------------------------------------------- */
function setupTasteInstrument() {
  const instrument = $("[data-taste-instrument]");
  if (!instrument) return;

  const tabs = $$("[data-taste-key]", instrument);
  const stage = $("[data-taste-stage]", instrument);
  const reading = $("#taste-reading", instrument);
  const label = $("[data-taste-label]", reading);
  const value = $("[data-taste-value]", reading);
  const copy = $("[data-taste-copy]", reading);
  if (!tabs.length || !stage || !reading) return;

  const select = (tab, { focus = false } = {}) => {
    tabs.forEach((item) => {
      const active = item === tab;
      item.setAttribute("aria-selected", String(active));
      item.tabIndex = active ? 0 : -1;
    });

    stage.dataset.tasteActive = tab.dataset.tasteKey || "gust";
    reading.setAttribute("aria-labelledby", tab.id);
    if (label) label.textContent = tab.textContent.trim();
    if (value) value.textContent = tab.dataset.tasteValue || "";
    if (copy) copy.textContent = tab.dataset.tasteCopy || "";
    if (focus) tab.focus({ preventScroll: true });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => select(tab));
    tab.addEventListener("keydown", (event) => {
      let next = null;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        next = tabs[(index + 1) % tabs.length];
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        next = tabs[(index - 1 + tabs.length) % tabs.length];
      }
      if (event.key === "Home") next = tabs[0];
      if (event.key === "End") next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      select(next, { focus: true });
    });
  });
}

/* --------------------------------------------------------------------------
   207D · Three Paths / One Table
   Desktop keeps every founder story open. Mobile starts scan-first, while
   native <details> keeps every full story one tap and one keyboard action away.
   -------------------------------------------------------------------------- */
function setupStoryPaths() {
  const paths = $("[data-story-paths]");
  if (!paths) return;

  const details = $$("details", paths);
  if (!details.length) return;

  const compact = window.matchMedia("(max-width: 760px)");
  let initialised = false;

  const paintAction = (item) => {
    const action = $(".chapter-path__action", item);
    if (action) action.textContent = item.open ? TOS_UI("ui_inchide", "Închide") : TOS_UI("ui_citeste", "Citește");
  };

  const arrange = () => {
    if (compact.matches) {
      if (!initialised) {
        details.forEach((item, index) => {
          item.open = index === 0;
        });
      }
    } else {
      details.forEach((item) => {
        item.open = true;
      });
    }
    details.forEach(paintAction);
    initialised = true;
  };

  details.forEach((item) => item.addEventListener("toggle", () => paintAction(item)));
  arrange();
  compact.addEventListener?.("change", arrange);
}

/* --------------------------------------------------------------------------
   207F · Open Table Concierge
   The reason changes guidance and optional context only. Name, email, subject,
   message and consent remain explicit; Shopify remains the submit owner.
   -------------------------------------------------------------------------- */
function setupConcierge() {
  const form = $("[data-concierge-form]");
  const guide = $("[data-concierge-guide]");
  if (!form || !guide) return;

  const radios = $$('input[name="contact[reason]"]', form);
  const conditional = $$("[data-concierge-show]", form);
  const subject = $("#contact-subject", form);
  const hint = $("[data-concierge-subject-hint]", form);
  const kicker = $("[data-concierge-kicker]", guide);
  const title = $("[data-concierge-title]", guide);
  const copy = $("[data-concierge-copy]", guide);

  const activeRadio = () => radios.find((radio) => radio.checked) || radios[0];

  const sync = () => {
    const radio = activeRadio();
    if (!radio) return;
    const reason = radio.value;
    const config = radio.dataset;
    form.dataset.conciergeReason = reason;

    if (kicker) kicker.textContent = config.conciergeKicker || "";
    if (title) title.textContent = config.conciergeTitle || "";
    if (copy) copy.textContent = config.conciergeCopy || "";
    if (subject) subject.placeholder = config.conciergePlaceholder || "";
    if (hint) hint.textContent = [form.dataset.conciergeExamplePrefix, config.conciergePlaceholder].filter(Boolean).join(" ");

    conditional.forEach((field) => {
      const show = String(field.dataset.conciergeShow || "")
        .split(/\s+/)
        .filter(Boolean)
        .includes(reason);
      field.hidden = !show;
      $$("input, select, textarea", field).forEach((control) => {
        control.disabled = !show;
      });
    });
  };

  radios.forEach((radio) => radio.addEventListener("change", sync));
  form.addEventListener("reset", () => window.setTimeout(sync, 0));
  sync();
}

function boot() {
  setupTasteInstrument();
  setupStoryPaths();
  setupConcierge();
}

/* R1C-M1-P1 — boot after first paint, own macrotask, same order */
scheduleBoot(boot);
